import { useFormContext, useWatch } from 'react-hook-form';
import {
    ApiOperatorTaskBeforeDeadlineMode,
    ApiOperatorTaskOverdueMode,
    ApiOperatorTaskType,
    type OperatorTaskOptionsResponse,
} from '@/core/generated/api';
import type { OperatorTaskFormValues } from '@/entities/OperatorTask/model/schema';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { EntityFormFields, EntityFormSection, EntityFormStack } from '@/shared/ui/EntityFormLayout/EntityFormLayout';

const { CheckboxField, DateField, TimeField, NumberField, SelectField } = createRHFFields<OperatorTaskFormValues>();

const beforeModes = [
    {
        id: ApiOperatorTaskBeforeDeadlineMode.DeadlineDay,
        name: 'В день дедлайна',
    },
    {
        id: ApiOperatorTaskBeforeDeadlineMode.Offset,
        name: 'За указанное число минут',
    },
];

const overdueModes = [
    {
        id: ApiOperatorTaskOverdueMode.Hourly,
        name: 'Каждые несколько часов',
    },
    {
        id: ApiOperatorTaskOverdueMode.Daily,
        name: 'Раз в день',
    },
];

interface TaskReminderFieldsProps {
    options: OperatorTaskOptionsResponse;
    originalIntervalHours?: number | null;
}

/** Настраивает напоминания до срока и при просрочке */
export const TaskReminderFields = ({ options, originalIntervalHours }: TaskReminderFieldsProps) => {
    const { control, formState } = useFormContext<OperatorTaskFormValues>();
    const values = useWatch({ control });
    const disabled = formState.isSubmitting;

    const intervalIds = options.overdueIntervalHours ?? [1, 2, 4, 8, 12, 24];

    const intervals = [...new Set([...intervalIds, ...(originalIntervalHours
        ? [originalIntervalHours]
        : [])])].map((id) => ({
            id,
            name: id === 1
                ? 'Каждый час'
                : `Каждые ${id} ч`,
        }));

    return (
        <EntityFormSection
            title="Напоминания"
            id="task-reminders-title"
        >
            <EntityFormStack>
                <p>Уведомления общие для всей смены</p>
                <CheckboxField
                    name="atDeadline"
                    label="В назначенное время"
                    disabled={disabled || values.type === ApiOperatorTaskType.OneTime}
                />
                {values.type === ApiOperatorTaskType.Deadline && (
                    <>
                        <CheckboxField
                            name="dailyEnabled"
                            label="Ежедневное напоминание"
                            disabled={disabled}
                        />
                        {values.dailyEnabled && (
                            <EntityFormFields>
                                <DateField
                                    name="dailyStartDate"
                                    label="Начиная с"
                                    isRequired
                                    disabled={disabled}
                                />
                                <TimeField
                                    name="dailyTime"
                                    label="Каждый день в"
                                    placeholder="10:00"
                                    isRequired
                                    disabled={disabled}
                                />
                            </EntityFormFields>
                        )}
                        <CheckboxField
                            name="beforeEnabled"
                            label="Дополнительно перед дедлайном"
                            disabled={disabled}
                        />
                        {values.beforeEnabled && (
                            <EntityFormFields>
                                <SelectField
                                    name="beforeMode"
                                    label="Когда напомнить"
                                    options={beforeModes}
                                    isRequired
                                    disabled={disabled}
                                />
                                {values.beforeMode === ApiOperatorTaskBeforeDeadlineMode.Offset
                                    ? (
                                        <NumberField
                                            name="beforeOffset"
                                            label="За сколько минут"
                                            min={1}
                                            max={options.maxBeforeDeadlineOffsetMinutes ?? 10080}
                                            isRequired
                                            disabled={disabled}
                                        />
                                    )
                                    : (
                                        <TimeField
                                            name="beforeTime"
                                            label="Время перед дедлайном"
                                            placeholder="09:00"
                                            isRequired
                                            disabled={disabled}
                                        />
                                    )}
                            </EntityFormFields>
                        )}
                    </>
                )}
                <CheckboxField
                    name="overdueEnabled"
                    label="Повторять при просрочке"
                    disabled={disabled}
                />
                {values.overdueEnabled && (
                    <EntityFormFields>
                        <SelectField
                            name="overdueMode"
                            label="Режим повтора"
                            options={overdueModes}
                            isRequired
                            disabled={disabled}
                        />
                        {values.overdueMode === ApiOperatorTaskOverdueMode.Hourly
                            ? (
                                <SelectField
                                    name="overdueHours"
                                    label="Частота повтора"
                                    options={intervals}
                                    isRequired
                                    disabled={disabled}
                                />
                            )
                            : (
                                <TimeField
                                    name="overdueTime"
                                    label="Время повторов"
                                    placeholder="10:00"
                                    isRequired
                                    disabled={disabled}
                                />
                            )}
                    </EntityFormFields>
                )}
            </EntityFormStack>
        </EntityFormSection>
    );
};
